/**
 * 4-column leads kanban per design README 5.8.
 *
 * Columns: TARGET, ACTIVE, CONVERTED, DEAD. Step 1 removed drag-to-move:
 * a lead's stage is derived from its Current Action, so the card reflows
 * columns when the action changes on its slide-over. Card clicks open the
 * slide-over.
 */

import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';

import { leadActionOrder, type LeadAction } from '@workspace/shared';

import { useLeads, type WireLead } from '@/lib/api/sales';
import { useSlideOver } from '@/lib/slide-over';
import { LeadCard } from './LeadCard';
import { NewLeadDialog } from './NewLeadDialog';

const COLUMNS: { stage: string; label: string }[] = [
  { stage: 'TARGET', label: 'TARGET' },
  { stage: 'ACTIVE', label: 'ACTIVE' },
  { stage: 'CONVERTED', label: 'CONVERTED' },
  { stage: 'DEAD', label: 'DEAD' },
];

export function LeadsBoard() {
  const { data, isLoading, isError } = useLeads();
  const openSlideOver = useSlideOver((s) => s.open);
  const [showNew, setShowNew] = useState(false);

  if (isLoading) return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  if (isError) return <p className="text-mx-body-sm text-mx-neg">Could not load leads.</p>;

  const leads = data?.leads ?? [];
  const byStage = new Map<string, WireLead[]>();
  for (const col of COLUMNS) byStage.set(col.stage, []);
  for (const lead of leads) byStage.get(lead.stage)?.push(lead);
  // Tabs-updates: order each column by Current Action (dropdown order),
  // value desc as a tie-break.
  for (const arr of byStage.values()) {
    arr.sort((a, b) => {
      const order =
        leadActionOrder(a.currentAction as LeadAction) -
        leadActionOrder(b.currentAction as LeadAction);
      return order !== 0 ? order : parseFloat(b.potentialValue) - parseFloat(a.potentialValue);
    });
  }

  return (
    <div className="flex h-[calc(100vh-12rem)] flex-col gap-mx-3">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="flex h-9 items-center gap-mx-2 bg-mx-orange px-mx-3 text-mx-body-sm font-medium text-mx-white hover:bg-mx-orange-hover"
        >
          <PlusIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />
          New lead
        </button>
      </div>
      <div className="flex flex-1 overflow-x-auto border-t border-mx-grey-800">
        {COLUMNS.map((col) => (
          <Column
            key={col.stage}
            stage={col.stage}
            label={col.label}
            leads={byStage.get(col.stage) ?? []}
            onCardClick={(id) => openSlideOver({ kind: 'lead', leadId: id })}
          />
        ))}
      </div>
      <NewLeadDialog open={showNew} onClose={() => setShowNew(false)} />
    </div>
  );
}

function Column({
  stage,
  label,
  leads,
  onCardClick,
}: {
  stage: string;
  label: string;
  leads: WireLead[];
  onCardClick: (id: string) => void;
}) {
  return (
    <div
      data-testid={`lead-column-${stage}`}
      className="flex h-full min-w-[260px] flex-1 flex-col border-r border-mx-grey-800 bg-mx-near-black px-mx-3 py-mx-4"
    >
      <p className="mono-label mb-mx-3 text-mx-grey-300">
        {label} <span className="text-mx-grey-mid">({leads.length})</span>
      </p>
      <ul className="flex flex-1 flex-col gap-mx-2 overflow-y-auto">
        {leads.map((lead) => (
          <li key={lead.id}>
            <LeadCard lead={lead} onClick={onCardClick} />
          </li>
        ))}
      </ul>
    </div>
  );
}
